const pad2 = (n) => (n < 10 ? "0" : "") + n;
const kilo = Math.pow(1000, 1);
const mega = Math.pow(1000, 2);
const giga = Math.pow(1000, 3);

export const YYYYMMDDHHmmssSSS = (date) =>
  date.getFullYear() +
  pad2(date.getMonth() + 1) +
  pad2(date.getDate()) +
  pad2(date.getHours()) +
  pad2(date.getMinutes()) +
  pad2(date.getSeconds());

export const unitToPrecisionMap = {
  Mbps: 3,
  ms: 3,
  '% retransimissions': 2,
  Flows: 0,
  'Flows-per-minute': 0,
  '% traffic at max speed': 2,
  '% congestion': 2
}

export function threeSignificantDigitsFormat(input, unit){
  if(isNaN(input)){
    return input;
  }

  const value = Number(input);
  const n = 3;
  let reduced = 0;
  let suffix = '';

  if(value < kilo || (value >= kilo && (unit === 'Mbps' || unit === 'ms'))){
    reduced = value;
  }else if(value >= kilo && value < mega){
    reduced = value/kilo;
    suffix = 'K';  
  }else if(value >= mega && value < giga){
    reduced = value/mega
    suffix = 'M';  
  }else if(value >= giga){
    reduced = value/giga;
    suffix = 'G';  
  }

  return reduced.toFixed(n-Math.min(n, reduced.toFixed(0).length)) + suffix;
}

export function toObjectLiteral(input) {
  return Object.fromEntries( input.entries() )
}

export const enumeration =
  (start = 1, extraParams) =>
  (item, index) => ({
    ...item,
    __id: start + index,
    ...extraParams,
  });

export const enumerate =
  (...params) =>
  (list) => list.map(enumeration(...params));

export const higherId = (current, { __id }) => (current > __id ? current : __id);

export const nextId = (items) => items.reduce(higherId, 0) + 1;


export const getFormData = (form) => {
  const data = new FormData(form);
  return Object.fromEntries(data.entries());
};

export function blockingWait(ms){
  if(isNaN(ms) || Number(ms) < 0){
    throw new Error('ms must be a number greater than 0')
  }

  const start = new Date().getTime();
  let current = start;
  while(current - start < ms){
    current = new Date().getTime();
  }
}

export const doesNothing = () => {};

// A promise and its resolvers, as Promise.withResolvers() gives them.
export const createPromiseWithResolve = () => {
  if (typeof Promise.withResolvers === "function") {
    const { promise, resolve, reject } = Promise.withResolvers();

    return [promise, resolve, reject];
  }

  let resolve;
  let reject;
  const promise = new Promise((solve, fail) => {
    resolve = solve;
    reject = fail;
  });

  return [promise, resolve, reject];
};

export const capitalize = input =>
  input.charAt(0).toUpperCase() + input.slice(1).toLowerCase();

export function getIndexes(header){
  return header.trim().split(/\s+/).reduce((acc, value, index) => {
    acc[value] = index;
    return acc;
  }, {});
}


export function createRectanglePath(props) {
  const {
    width,
    height,
    topLeftRadius,
    topRightRadius,
    bottomRightRadius,
    bottomLeftRadius,
  } = props;

  return [
    // Move to the start point, considering top left radius
    `M ${topLeftRadius} 0`,
    // Draw a horizontal line to the top right corner, considering top right radius
    `H ${width - topRightRadius}`,
    // Draw an arc for top right corner if radius is greater than 0
    topRightRadius > 0
      ? `A ${topRightRadius} ${topRightRadius} 0 0 1 ${width} ${topRightRadius}`
      : null,
    // Draw a vertical line to the bottom right corner, considering bottom right radius
    `V ${height - bottomRightRadius}`,
    // Draw an arc for bottom right corner if radius is greater than 0
    bottomRightRadius > 0
      ? `A ${bottomRightRadius} ${bottomRightRadius} 0 0 1 ${
          width - bottomRightRadius
        } ${height}`
      : null,
    // Draw a horizontal line to the bottom left corner, considering bottom left radius
    `H ${bottomLeftRadius}`,
    // Draw an arc for bottom left corner if radius is greater than 0
    bottomLeftRadius > 0
      ? `A ${bottomLeftRadius} ${bottomLeftRadius} 0 0 1 0 ${
          height - bottomLeftRadius
        }`
      : null,
    // Draw a vertical line to the top left corner, considering top left radius
    `V ${topLeftRadius}`,
    // Draw an arc for top left corner if radius is greater than 0
    topLeftRadius > 0
      ? `A ${topLeftRadius} ${topLeftRadius} 0 0 1 ${topLeftRadius} 0`
      : null,
    // Close the path
    "Z",
  ]
    .filter((v) => v != null)
    .join(" ");
}
const fromHex = (part) => parseInt(part, 16) || 0;

const expandToLength = (value, index, array) =>
  value === "" ? new Array(8 - (array.length - 1)).fill(null) : value;

export const atoiv6 = (value) =>
  value
    .split(":")
    .map(expandToLength)
    .flat()
    .reduce(
      (prev, part, idx) =>
        prev + BigInt(fromHex(part) * Math.pow(65536, 7 - idx)),
      BigInt(0)
    );

export function atoiv4(value) {
  return Number(
    value
      .split(".")
      .reduce((prev, part, idx) => prev + part * Math.pow(256, 3 - idx), 0)
  );
}

const sumToSeconds = (result, current, index) =>
  result + current * Math.pow(60, 2 - index);

export function durationToInt(value) {
  return value.split(":").reduce(sumToSeconds, 0);
};

export function intToDuration(input) {
  let value = input;
  const fillValue = (_, index) => {
    const weigth = Math.pow(60, 2 - index);
    const part = index < 2 ? Math.floor(value / weigth) : value;
    value = value - part * weigth;
    return index > 0 ? String(part).padStart(2, "0") : String(part);
  };
  return Array(3).fill(0).map(fillValue).join(":");
}

const _localeSettings = [
  "en-US",
  {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  },
];

export const expressAsDataSpeed = (value) =>
  value > 999999
    ? `${Number(value / 1000000).toLocaleString(..._localeSettings)} Gbps`
    : value > 999
    ? `${Number(value / 1000).toLocaleString(..._localeSettings)} Mbps`
    : `${Number(value).toLocaleString(..._localeSettings)} Kbps`;

export const parseIP = (value) =>
  (!value) ? 0 :
    value.includes(":") ? atoiv6(value) : atoiv4(value);
